import React, { useState, useEffect, useCallback, useRef } from "react";

/**
 * Taking something back.
 *
 * Asking "are you sure?" before a delete is the cheap version of this. It
 * interrupts every delete to guard against the rare wrong one, and people
 * learn to click through it, at which point it guards against nothing.
 * Letting the delete happen and offering to reverse it costs the careful
 * person nothing and gives the careless one a few seconds — which is when
 * they notice.
 *
 * One thing at a time. A second delete replaces the offer rather than
 * queueing behind it; the previous one is committed. Anything more is a
 * history, and this is a chance, not a history.
 */
const UNDO_MS = 6000;

export function useUndo(ms = UNDO_MS) {
  const [pending, setPending] = useState(null);   // { label, restore }
  const timer = useRef(null);

  const dismiss = useCallback(() => {
    clearTimeout(timer.current);
    setPending(null);
  }, []);

  /**
   * @param label   what was removed, in words a person used — "Umbreon VMAX"
   * @param restore called if they take it back; must put the thing back
   */
  const offer = useCallback((label, restore) => {
    clearTimeout(timer.current);
    setPending({ label, restore });
    timer.current = setTimeout(() => setPending(null), ms);
  }, [ms]);

  const undo = useCallback(() => {
    if (!pending) return;
    clearTimeout(timer.current);
    pending.restore();
    setPending(null);
  }, [pending]);

  useEffect(() => () => clearTimeout(timer.current), []);

  return { pending, offer, undo, dismiss };
}

/**
 * The offer itself. role="status" so a screen reader hears it without being
 * dragged to it; the button is the only thing that needs reaching.
 */
export function UndoBar({ pending, onUndo, verb = "Removed" }) {
  if (!pending) return null;
  return (
    <div className="undo-bar" role="status">
      <span className="undo-text">
        {verb} <strong>{pending.label}</strong>
      </span>
      <button type="button" className="btn btn-sm btn-secondary undo-btn" onClick={onUndo}>
        Undo
      </button>
    </div>
  );
}
